<script>
  /* global Global */
  import Header from '../common/Header.svelte';
  import Footer from '../common/Footer.svelte';
  import Loading from '../common/Loading.svelte';
  import Error from '../common/Error.svelte';
  import Title from '../common/Title.svelte';
  import YearsSelect from '../ui-components/YearsSelect.svelte';
  import SmallTitle from '../common/SmallTitle.svelte';
  import StandingLegend from '../ui-components/standings/StandingLegend.svelte';
  import Standing from '../ui-components/standings/Standing.svelte';
  import Button from '../common/Button.svelte';
  import LiveGamesBox from '../ui-components/LiveGamesBox.svelte';
  import PageBox from '../ui-components/PageBox.svelte';
  import Common from '../js/common.js';

  export let params;

  if (!Global.pages.includes('standings')) {
    window.location.href = window.localStorage.getItem('currentPage') || './';
  }

  let lang;

  let user = JSON.parse(window.localStorage.getItem('user'));
  const selectedYear = Number(params?.year) || Global.years[0];

  let standings;
  let page;

  let loading = true;
  let error = false;

  (async () => {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/standings/get/${selectedYear}`);

    if (response.ok) {
      const data = await response.json();

      standings = data.standings;
      page = data.page;
      user = data.user;

      if (user) window.localStorage.setItem('user', JSON.stringify(data.user));
      else window.localStorage.removeItem('user');
    } else {
      error = true;
    }

    loading = false;
  })();

  async function updateStandings () {
    // eslint-disable-next-line no-undef
    await fetch(`${Common.BACKEND_URL}/standings/update`);
  }

  const i18n = {
    pageTitle: {
      cs: `Umístění | ${Global.project.name.cs}`,
      en: `Standings | ${Global.project.name.en}`,
    },
    title: {
      cs: 'Umístění',
      en: 'Standings',
    },
    finalStandings: {
      cs: 'Konečné umístění',
      en: 'Final standings',
    },
    group: {
      cs: 'Skupina',
      en: 'Group',
    },
    notAvailableYet: {
      cs: 'Umístění zatím není k dispozici.',
      en: 'Standings are not available yet.',
    },
    notAvailableYear: {
      cs: `Umístění pro ročník ${selectedYear} není k dispozici.`,
      en: `Standings for year ${selectedYear} are not available.`,
    },
    liveGames: {
      cs: 'Živé zápasy',
      en: 'Live games',
    },
    updateStandings: {
      cs: 'Aktualizovat umístění',
      en: 'Update standings',
    },
  };

  const projectStyle = Global.project.style;
  const years = Global.years;
</script>

<svelte:head>
  <title> {i18n.pageTitle[lang]} </title>
</svelte:head>

<Header page='standings{selectedYear !== years[0] ? `/${selectedYear}` : ''}' bind:lang bind:user/>

<Title title={i18n.title[lang]} color={projectStyle === 'sharp' ? 'blue' : 'red'} img='title/standings.jpg' bind:lang bind:user/>

<div class='standings'>
  <Loading bind:display={loading}/>
  <Error bind:display={error} bind:lang/>

  {#if !loading && !error}
    <YearsSelect page='standings/:year' selectedYear={selectedYear} years={years} bind:lang/>

    <div class='standings-content'>
      <div class='standings-content-main'>
        {#each Object.keys(standings) as key}
          <div class='standings-content-main-final-or-group' class:standings-content-main-final-or-group-odd={Object.keys(standings).length % 2}>
            <div class='standings-content-main-final-or-group-small-title'>
              {#if key === 'final'}
                <SmallTitle value={i18n.finalStandings[lang]}/>
              {:else}
                <SmallTitle value={`${i18n.group[lang]} ${key.toUpperCase()}`}/>
              {/if}
            </div>

            <div class='standings-content-main-final-or-group-content'>
              <div class='standings-content-main-final-or-group-content-standing-legend'>
                <StandingLegend bind:lang/>
              </div>

              {#each standings[key] as standing}
                <div class='standings-content-main-final-or-group-content-standing'>
                  <Standing value={standing} bind:lang/>
                </div>
              {/each}
            </div>
          </div>
        {:else}
          <div class='standings-content-main-not-available'>
            {#if selectedYear === years[0]}
              {i18n.notAvailableYet[lang]}
            {:else}
              {i18n.notAvailableYear[lang]}
            {/if}
          </div>
        {/each}

        {#if user}
          <div class='standings-content-main-admin'>
            <Button value={i18n.updateStandings[lang]} icon='refresh' admin={true} disabled={!user.roles.includes('ROLE_ADMIN')}
              onClick={async () => { await updateStandings(); }}/>
          </div>
        {/if}
      </div>

      <div class='standings-content-right-panel'>
        <SmallTitle value={i18n.liveGames[lang]}/>

        <LiveGamesBox/>
      </div>
    </div>

    <PageBox value={page} page='standings' bind:lang bind:user/>
  {/if}
</div>

<Footer bind:lang/>

<style>
  .standings {
    display: flex;
    flex-direction: column;
    gap: 40px;

    max-width: 1450px;

    padding: 40px 60px;

    margin: auto;
  }

  .standings-content {
    display: flex;
    justify-content: space-between;
    gap: 40px;
  }

  .standings-content-main {
    display: flex;
    flex-wrap: wrap;
    flex-grow: 1;
    gap: 40px;
  }

  .standings-content-main-final-or-group {
    display: flex;
    flex-direction: column;
    gap: 20px;

    width: calc((100% - 40px) / 2);
  }

  .standings-content-main-final-or-group-odd:first-child {
    width: 100%;
  }

  .standings-content-main-final-or-group-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .standings-content-main-final-or-group-content-standing-legend {
    margin-bottom: -5px;
  }

  .standings-content-main-not-available {
    display: flex;
    justify-content: center;
    align-items: center;

    width: 100%;
    height: 100%;
    min-height: 22.5px;

    text-align: center;
    text-transform: uppercase;

    letter-spacing: 1.2px;
  }

  .standings-content-main-admin {
    width: 100%;
  }

  .standings-content-right-panel {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 40px;

    width: 375px;
  }

  @media only screen and (max-width: 1400px) {
    .standings-content-main-final-or-group {
      width: 100%;
    }
  }

  @media only screen and (max-width: 950px) {
    .standings-content-right-panel {
      display: none;
    }
  }

  @media only screen and (max-width: 900px) {
    .standings {
      padding: 40px;
    }
  }

  @media only screen and (max-width: 350px) {
    .standings {
      padding: 40px 20px;
    }
  }
</style>
